import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes';
import appConfig from "../../app.config";

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication using local Laravel API
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  // Check for token passed from portal via URL query parameter
  const urlCode = routeTo.query.code;

  if (urlCode) {
    try {
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
      const response = await axios.post(`${apiUrl}/api/portal-auth`, { code: urlCode });

      const { token, user } = response.data;

      localStorage.setItem('auth_token', token);
      sessionStorage.setItem('authUser', JSON.stringify(user));
      localStorage.setItem('user', JSON.stringify(user));

      // Redirect to dashboard without the token in the URL
      return next({ name: 'default', replace: true });
    } catch (error) {
      console.error('Portal authentication failed:', error);
      return next({ name: 'login', replace: true });
    }
  }

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  // If auth isn't required, continue
  if (!authRequired) return next();



  // Check for token in localStorage
  const token = localStorage.getItem('auth_token');

  const user = sessionStorage.getItem('authUser');

  // If no token, redirect to login
  if (!token) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // Check lock screen state
  const isLocked = sessionStorage.getItem('screen_locked') === 'true';

  if (isLocked && routeTo.name !== 'lock-screen' && routeTo.name !== 'login') {
    // User is locked, redirect to lock screen
    return next({ name: 'lock-screen' });
  }

  if (!isLocked && routeTo.name === 'lock-screen') {
    // Not locked but trying to access lock screen, go home
    return next({ name: 'default' });
  }

  // If we have a token and user, continue
  if (token && user) {
    return next();
  }

  // If we have token but no user, try to fetch user from API
  try {
    const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
    const response = await axios.get(`${apiUrl}/api/user`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    sessionStorage.setItem('authUser', JSON.stringify(response.data));
    localStorage.setItem('user', JSON.stringify(response.data));
    next();
  } catch (error) {
    // Token invalid, clear and redirect to login
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('authUser');
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }
});



router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
